import React, { useCallback, useEffect, useMemo, useState } from "react";
import {
  Ship,
  BedDouble,
  Map as MapIcon,
  CalendarDays,
  Inbox,
  IndianRupee,
  TrendingUp,
  TrendingDown,
  Users,
  RefreshCw,
  AlertCircle,
  AlertTriangle,
  CheckCircle2,
  ArrowRight,
  Anchor,
  CircleDot,
  Loader2,
} from "lucide-react";
import { api } from "../api/axios";
import "./Cruisedashboard.css";

/* =========================================================
   API
   (sab endpoints wahi hain jo baaki cruise pages use karte hain)
========================================================= */

const ENDPOINTS = {
  ships: "/api/vendor/cruise/ships",
  cabins: "/api/cruise-cabins",
  itineraries: "/api/cruise-itineraries",
  sailings: "/api/cruise-sailings",
  bookings: "/api/vendor/cruise-bookings",
};

const authConfig = () => ({
  headers: { Authorization: `Bearer ${localStorage.getItem("vendorToken")}` },
});

const listFrom = (data, key) => {
  if (Array.isArray(data)) return data;
  if (Array.isArray(data?.[key])) return data[key];
  if (Array.isArray(data?.data?.[key])) return data.data[key];
  if (Array.isArray(data?.data)) return data.data;
  return [];
};

/* =========================================================
   HELPERS
========================================================= */

const DAY = 24 * 60 * 60 * 1000;

const startOfDay = (value) => {
  const d = new Date(value);
  d.setHours(0, 0, 0, 0);
  return d;
};

const dayKey = (value) => {
  const d = new Date(value);
  const pad = (n) => String(n).padStart(2, "0");
  return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}`;
};

const validDate = (value) => {
  if (!value) return null;
  const d = new Date(value);
  return Number.isNaN(d.getTime()) ? null : d;
};

const money = (value, compact = false) => {
  const n = Number(value || 0);
  try {
    return new Intl.NumberFormat("en-IN", {
      style: "currency",
      currency: "INR",
      maximumFractionDigits: 0,
      ...(compact && n >= 100000 ? { notation: "compact", maximumFractionDigits: 1 } : {}),
    }).format(n);
  } catch {
    return `₹${Math.round(n).toLocaleString("en-IN")}`;
  }
};

const dateText = (value) => {
  const d = validDate(value);
  return d
    ? d.toLocaleDateString("en-IN", { day: "2-digit", month: "short", year: "numeric" })
    : "—";
};

const shortDay = (key) => {
  const d = new Date(`${key}T00:00:00`);
  return d.toLocaleDateString("en-IN", { day: "numeric", month: "short" });
};

const greeting = () => {
  const h = new Date().getHours();
  if (h < 12) return "Good morning";
  if (h < 17) return "Good afternoon";
  return "Good evening";
};

const readVendorName = () => {
  try {
    const v = JSON.parse(localStorage.getItem("vendorUser") || "{}");
    return (
      v.business?.businessName || v.businessName || v.companyName || v.name || v.ownerName || ""
    );
  } catch {
    return "";
  }
};

const isCancelled = (b) => b?.bookingStatus === "cancelled";
const isIncoming = (b) => ["pending", "reserved"].includes(b?.bookingStatus);
const isRevenue = (b) => b?.paymentStatus === "paid" && !isCancelled(b);
const guestsOf = (b) => Number(b?.quantity) || b?.passengers?.length || 0;

const delta = (current, previous) => {
  if (!previous && !current) return { pct: 0, dir: "flat" };
  if (!previous) return { pct: 100, dir: "up" };
  const pct = ((current - previous) / previous) * 100;
  return { pct: Math.abs(pct), dir: pct > 0.5 ? "up" : pct < -0.5 ? "down" : "flat" };
};

const shipNameOf = (s) => s?.shipName || s?.name || "Cruise ship";
const sailingCodeOf = (s) => s?.sailingCode || s?.code || "Sailing";

/* =========================================================
   COMPONENT
========================================================= */

export default function CruiseDashboard({ onNavigate = () => {} }) {
  const [data, setData] = useState({
    ships: [],
    cabins: [],
    itineraries: [],
    sailings: [],
    bookings: [],
  });
  const [loading, setLoading] = useState(true);
  const [refreshing, setRefreshing] = useState(false);
  const [failed, setFailed] = useState([]);
  const [updatedAt, setUpdatedAt] = useState(null);

  const [range, setRange] = useState(7); // 7 | 30 days
  const [metric, setMetric] = useState("revenue"); // revenue | bookings

  const vendorName = useMemo(readVendorName, []);

  /* ---------- load ---------- */
  const load = useCallback(async (isRefresh = false) => {
    if (isRefresh) setRefreshing(true);
    else setLoading(true);

    const keys = Object.keys(ENDPOINTS);
    const results = await Promise.allSettled(
      keys.map((k) => api.get(ENDPOINTS[k], authConfig()))
    );

    const next = {};
    const bad = [];
    results.forEach((res, i) => {
      const key = keys[i];
      if (res.status === "fulfilled") {
        next[key] = listFrom(res.value.data, key);
      } else {
        next[key] = [];
        bad.push(key);
      }
    });

    setData(next);
    setFailed(bad);
    setUpdatedAt(new Date());
    setLoading(false);
    setRefreshing(false);
  }, []);

  useEffect(() => {
    load();
  }, [load]);

  /* ---------- derived ---------- */
  const d = useMemo(() => {
    const { ships, cabins, itineraries, sailings, bookings } = data;
    const today = startOfDay(new Date());
    const todayKey = dayKey(today);

    /* range totals + previous period */
    const inRange = (b, from, to) => {
      const c = validDate(b.createdAt);
      return c && c >= from && c < to;
    };
    const curFrom = new Date(today.getTime() - (range - 1) * DAY);
    const curTo = new Date(today.getTime() + DAY);
    const prevFrom = new Date(curFrom.getTime() - range * DAY);

    const cur = bookings.filter((b) => inRange(b, curFrom, curTo) && !isCancelled(b));
    const prev = bookings.filter((b) => inRange(b, prevFrom, curFrom) && !isCancelled(b));

    const sumRevenue = (list) =>
      list.filter(isRevenue).reduce((s, b) => s + Number(b.totalAmount || 0), 0);

    const revenue = sumRevenue(cur);
    const prevRevenue = sumRevenue(prev);

    /* chart series */
    const series = [];
    for (let i = range - 1; i >= 0; i -= 1) {
      series.push({ key: dayKey(new Date(today.getTime() - i * DAY)), revenue: 0, bookings: 0 });
    }
    const byKey = new Map(series.map((s) => [s.key, s]));
    cur.forEach((b) => {
      const slot = byKey.get(dayKey(b.createdAt));
      if (!slot) return;
      slot.bookings += 1;
      if (isRevenue(b)) slot.revenue += Number(b.totalAmount || 0);
    });

    /* today */
    const todayBookings = bookings.filter(
      (b) => !isCancelled(b) && validDate(b.createdAt) && dayKey(b.createdAt) === todayKey
    );
    const todayRevenue = sumRevenue(todayBookings);

    /* booking status breakdown */
    const status = { incoming: 0, confirmed: 0, completed: 0, cancelled: 0 };
    bookings.forEach((b) => {
      if (isIncoming(b)) status.incoming += 1;
      else if (b.bookingStatus === "confirmed") status.confirmed += 1;
      else if (b.bookingStatus === "completed") status.completed += 1;
      else if (isCancelled(b)) status.cancelled += 1;
    });

    const pendingValue = bookings
      .filter((b) => !isCancelled(b) && b.bookingStatus !== "completed" && !isRevenue(b))
      .reduce((s, b) => s + Number(b.totalAmount || 0), 0);

    /* sailings */
    const upcoming = sailings
      .filter((s) => {
        const dep = validDate(s.departureDate);
        return dep && dep >= today && !["cancelled", "completed"].includes(s.status);
      })
      .sort((a, b) => new Date(a.departureDate) - new Date(b.departureDate));

    const capTotal = upcoming.reduce((s, x) => s + Number(x.totalCapacity || 0), 0);
    const capAvail = upcoming.reduce((s, x) => s + Number(x.availableCapacity || 0), 0);
    const occupancy = capTotal > 0 ? Math.round(((capTotal - capAvail) / capTotal) * 100) : 0;

    const departingSoon = upcoming.filter(
      (s) => new Date(s.departureDate) - today <= 7 * DAY
    ).length;

    const nearlyFull = upcoming.filter((s) => {
      const t = Number(s.totalCapacity || 0);
      const a = Number(s.availableCapacity || 0);
      return t > 0 && (a === 0 || a / t <= 0.1);
    });

    const upcomingGuests = bookings
      .filter((b) => ["reserved", "confirmed"].includes(b.bookingStatus))
      .filter((b) => {
        const dep = validDate(b.sailingId?.departureDate);
        return dep && dep >= today;
      })
      .reduce((s, b) => s + guestsOf(b), 0);

    /* fleet */
    const countBy = (list, st) => list.filter((x) => x.status === st).length;

    /* needs attention */
    const unpaidConfirmed = bookings.filter(
      (b) => b.bookingStatus === "confirmed" && b.paymentStatus !== "paid"
    ).length;

    const attention = [];
    if (status.incoming > 0)
      attention.push({
        tone: "warn",
        text: `${status.incoming} booking request${status.incoming > 1 ? "s" : ""} waiting for your confirmation`,
        action: "Review",
        target: "cruise_bookings",
      });
    if (unpaidConfirmed > 0)
      attention.push({
        tone: "warn",
        text: `${unpaidConfirmed} confirmed booking${unpaidConfirmed > 1 ? "s" : ""} with payment not received`,
        action: "Check",
        target: "cruise_bookings",
      });
    if (departingSoon > 0)
      attention.push({
        tone: "info",
        text: `${departingSoon} sailing${departingSoon > 1 ? "s" : ""} departing in the next 7 days`,
        action: "View",
        target: "cruise_sailings",
      });
    if (nearlyFull.length > 0)
      attention.push({
        tone: "info",
        text: `${nearlyFull.length} sailing${nearlyFull.length > 1 ? "s are" : " is"} sold out or almost full`,
        action: "View",
        target: "cruise_sailings",
      });
    [
      ["ships", ships, "ship", "cruise_manage"],
      ["cabins", cabins, "cabin", "cruise_cabins"],
      ["itineraries", itineraries, "itinerary", "cruise_itineraries"],
      ["sailings", sailings, "sailing", "cruise_sailings"],
    ].forEach(([, list, label, target]) => {
      const drafts = countBy(list, "draft");
      if (drafts > 0)
        attention.push({
          tone: "muted",
          text: `${drafts} ${label}${drafts > 1 ? "s are" : " is"} still in draft`,
          action: "Open",
          target,
        });
    });

    /* setup progress */
    const steps = [
      { label: "Add a cruise ship", done: ships.length > 0, target: "cruise_manage" },
      { label: "Add cabins", done: cabins.length > 0, target: "cruise_cabins" },
      { label: "Create an itinerary", done: itineraries.length > 0, target: "cruise_itineraries" },
      { label: "Schedule a sailing", done: sailings.length > 0, target: "cruise_sailings" },
      { label: "Receive your first booking", done: bookings.length > 0, target: "cruise_bookings" },
    ];

    const recent = [...bookings]
      .sort((a, b) => new Date(b.createdAt || 0) - new Date(a.createdAt || 0))
      .slice(0, 6);

    return {
      revenue,
      revenueDelta: delta(revenue, prevRevenue),
      bookingsCount: cur.length,
      bookingsDelta: delta(cur.length, prev.length),
      series,
      todayBookings: todayBookings.length,
      todayRevenue,
      status,
      pendingValue,
      upcoming,
      occupancy,
      capTotal,
      departingSoon,
      upcomingGuests,
      fleet: {
        ships: { total: ships.length, active: countBy(ships, "active"), draft: countBy(ships, "draft") },
        cabins: { total: cabins.length, active: countBy(cabins, "active"), draft: countBy(cabins, "draft") },
        itineraries: { total: itineraries.length, active: countBy(itineraries, "active"), draft: countBy(itineraries, "draft") },
        sailings: { total: sailings.length, active: countBy(sailings, "active"), draft: countBy(sailings, "draft") },
      },
      attention,
      steps,
      recent,
    };
  }, [data, range]);

  const allFailed = failed.length === Object.keys(ENDPOINTS).length;
  const setupDone = d.steps.filter((s) => s.done).length;
  const maxBar = Math.max(1, ...d.series.map((s) => (metric === "revenue" ? s.revenue : s.bookings)));
  const statusTotal = Object.values(d.status).reduce((a, b) => a + b, 0);

  /* ---------- loading skeleton ---------- */
  if (loading) {
    return (
      <div className="cdb">
        <div className="cdb-loading">
          <Loader2 size={30} className="cdb-spin" />
          <span>Loading your cruise dashboard...</span>
        </div>
      </div>
    );
  }

  /* ============================================================ RENDER */
  return (
    <div className="cdb">
      {/* heading */}
      <div className="cdb-head">
        <div>
          <h1>
            {greeting()}
            {vendorName ? `, ${vendorName}` : ""}
          </h1>
          <p>
            Here is how your cruise business is performing
            {updatedAt && (
              <span className="cdb-updated">
                {" "}
                • Updated {updatedAt.toLocaleTimeString("en-IN", { hour: "2-digit", minute: "2-digit" })}
              </span>
            )}
          </p>
        </div>

        <div className="cdb-head-actions">
          <div className="cdb-segment" role="tablist" aria-label="Date range">
            {[7, 30].map((n) => (
              <button
                key={n}
                role="tab"
                aria-selected={range === n}
                className={range === n ? "active" : ""}
                onClick={() => setRange(n)}
              >
                {n} days
              </button>
            ))}
          </div>
          <button className="cdb-refresh" onClick={() => load(true)} disabled={refreshing}>
            <RefreshCw size={16} className={refreshing ? "cdb-spin" : ""} />
            Refresh
          </button>
        </div>
      </div>

      {allFailed && (
        <div className="cdb-alert cdb-alert-error">
          <AlertCircle size={18} />
          <span>
            Could not load dashboard data. Please check your connection or log in again, then press
            Refresh.
          </span>
        </div>
      )}
      {!allFailed && failed.length > 0 && (
        <div className="cdb-alert cdb-alert-warn">
          <AlertTriangle size={18} />
          <span>
            Some data could not be loaded ({failed.join(", ")}). Numbers on this page may be
            incomplete.
          </span>
        </div>
      )}

      {/* setup progress */}
      {setupDone < d.steps.length && !allFailed && (
        <section className="cdb-card cdb-setup">
          <div className="cdb-card-head">
            <div>
              <h2>Finish setting up your cruise business</h2>
              <p>
                {setupDone} of {d.steps.length} steps completed
              </p>
            </div>
            <div className="cdb-progress">
              <span style={{ width: `${(setupDone / d.steps.length) * 100}%` }} />
            </div>
          </div>
          <div className="cdb-steps">
            {d.steps.map((s) => (
              <button
                key={s.label}
                className={`cdb-step ${s.done ? "done" : ""}`}
                onClick={() => onNavigate(s.target)}
              >
                {s.done ? <CheckCircle2 size={18} /> : <CircleDot size={18} />}
                <span>{s.label}</span>
              </button>
            ))}
          </div>
        </section>
      )}

      {/* KPI cards */}
      <section className="cdb-kpis">
        <Kpi
          icon={<IndianRupee size={20} />}
          tone="blue"
          title={`Revenue (last ${range} days)`}
          value={money(d.revenue, true)}
          change={d.revenueDelta}
          foot={`Pending payments: ${money(d.pendingValue, true)}`}
          onClick={() => onNavigate("cruise_bookings")}
        />
        <Kpi
          icon={<CalendarDays size={20} />}
          tone="green"
          title={`Bookings (last ${range} days)`}
          value={d.bookingsCount}
          change={d.bookingsDelta}
          foot={`Today: ${d.todayBookings} booking${d.todayBookings === 1 ? "" : "s"} • ${money(d.todayRevenue, true)}`}
          onClick={() => onNavigate("cruise_bookings")}
        />
        <Kpi
          icon={<Inbox size={20} />}
          tone="amber"
          title="Incoming requests"
          value={d.status.incoming}
          foot={d.status.incoming > 0 ? "Waiting for your confirmation" : "You are all caught up"}
          highlight={d.status.incoming > 0}
          onClick={() => onNavigate("cruise_bookings")}
        />
        <Kpi
          icon={<Users size={20} />}
          tone="violet"
          title="Seats filled (upcoming)"
          value={d.capTotal > 0 ? `${d.occupancy}%` : "—"}
          foot={`${d.upcomingGuests} guest${d.upcomingGuests === 1 ? "" : "s"} booked on upcoming sailings`}
          onClick={() => onNavigate("cruise_sailings")}
        />
      </section>

      {/* chart + status */}
      <section className="cdb-grid cdb-grid-2-1">
        <article className="cdb-card">
          <div className="cdb-card-head">
            <div>
              <h2>{metric === "revenue" ? "Revenue" : "Bookings"} trend</h2>
              <p>Last {range} days, by booking date</p>
            </div>
            <div className="cdb-segment small">
              <button className={metric === "revenue" ? "active" : ""} onClick={() => setMetric("revenue")}>
                Revenue
              </button>
              <button className={metric === "bookings" ? "active" : ""} onClick={() => setMetric("bookings")}>
                Bookings
              </button>
            </div>
          </div>

          {d.series.every((s) => s.revenue === 0 && s.bookings === 0) ? (
            <div className="cdb-empty small">
              <TrendingUp size={28} />
              <p>No bookings in this period yet.</p>
            </div>
          ) : (
            <div className={`cdb-chart ${range === 30 ? "dense" : ""}`}>
              {d.series.map((s, i) => {
                const value = metric === "revenue" ? s.revenue : s.bookings;
                const showLabel = range === 7 || i % 5 === 0 || i === d.series.length - 1;
                return (
                  <div className="cdb-bar-col" key={s.key}>
                    <div className="cdb-bar-wrap">
                      <div
                        className="cdb-bar"
                        style={{ height: `${Math.max((value / maxBar) * 100, value > 0 ? 4 : 0)}%` }}
                        title={`${shortDay(s.key)}: ${
                          metric === "revenue" ? money(value) : `${value} booking${value === 1 ? "" : "s"}`
                        }`}
                      />
                    </div>
                    <span className="cdb-bar-label">{showLabel ? shortDay(s.key) : ""}</span>
                  </div>
                );
              })}
            </div>
          )}
        </article>

        <article className="cdb-card">
          <div className="cdb-card-head">
            <div>
              <h2>Booking status</h2>
              <p>{statusTotal} total booking{statusTotal === 1 ? "" : "s"}</p>
            </div>
          </div>

          {statusTotal === 0 ? (
            <div className="cdb-empty small">
              <Inbox size={28} />
              <p>No bookings yet.</p>
            </div>
          ) : (
            <>
              <div className="cdb-stack">
                {[
                  ["incoming", "Incoming"],
                  ["confirmed", "Confirmed"],
                  ["completed", "Completed"],
                  ["cancelled", "Cancelled"],
                ].map(([k]) =>
                  d.status[k] > 0 ? (
                    <span
                      key={k}
                      className={`cdb-stack-seg ${k}`}
                      style={{ width: `${(d.status[k] / statusTotal) * 100}%` }}
                      title={`${k}: ${d.status[k]}`}
                    />
                  ) : null
                )}
              </div>
              <ul className="cdb-legend">
                {[
                  ["incoming", "Incoming"],
                  ["confirmed", "Confirmed"],
                  ["completed", "Completed"],
                  ["cancelled", "Cancelled"],
                ].map(([k, label]) => (
                  <li key={k}>
                    <button onClick={() => onNavigate("cruise_bookings")}>
                      <span className={`cdb-dot ${k}`} />
                      <span className="cdb-legend-label">{label}</span>
                      <strong>{d.status[k]}</strong>
                    </button>
                  </li>
                ))}
              </ul>
            </>
          )}
        </article>
      </section>

      {/* fleet */}
      <section className="cdb-fleet">
        <FleetCard icon={<Ship size={20} />} label="Cruise ships" stat={d.fleet.ships} onClick={() => onNavigate("cruise_manage")} />
        <FleetCard icon={<BedDouble size={20} />} label="Cabins" stat={d.fleet.cabins} onClick={() => onNavigate("cruise_cabins")} />
        <FleetCard icon={<MapIcon size={20} />} label="Itineraries" stat={d.fleet.itineraries} onClick={() => onNavigate("cruise_itineraries")} />
        <FleetCard icon={<Anchor size={20} />} label="Sailings" stat={d.fleet.sailings} onClick={() => onNavigate("cruise_sailings")} />
      </section>

      {/* upcoming sailings + attention */}
      <section className="cdb-grid cdb-grid-2-1">
        <article className="cdb-card">
          <div className="cdb-card-head">
            <div>
              <h2>Upcoming sailings</h2>
              <p>Next departures and how full they are</p>
            </div>
            <button className="cdb-link" onClick={() => onNavigate("cruise_sailings")}>
              View all <ArrowRight size={15} />
            </button>
          </div>

          {d.upcoming.length === 0 ? (
            <div className="cdb-empty small">
              <Anchor size={28} />
              <p>No upcoming sailings scheduled.</p>
              <button className="cdb-btn" onClick={() => onNavigate("cruise_sailings")}>
                Schedule a sailing
              </button>
            </div>
          ) : (
            <ul className="cdb-sailings">
              {d.upcoming.slice(0, 5).map((s) => {
                const total = Number(s.totalCapacity || 0);
                const avail = Number(s.availableCapacity || 0);
                const booked = Math.max(total - avail, 0);
                const pct = total > 0 ? Math.round((booked / total) * 100) : 0;
                const tone = pct >= 90 ? "hot" : pct >= 60 ? "mid" : "low";
                return (
                  <li key={s._id} onClick={() => onNavigate("cruise_sailings")}>
                    <div className="cdb-sailing-main">
                      <strong>{sailingCodeOf(s)}</strong>
                      <span>
                        {shipNameOf(s.shipId)} • {s.itineraryId?.itineraryName || s.itineraryId?.name || "Itinerary"}
                      </span>
                      <small>
                        {dateText(s.departureDate)} →{" "}
                        {s.departurePort?.city || s.departurePort?.name || "—"} to{" "}
                        {s.arrivalPort?.city || s.arrivalPort?.name || "—"}
                      </small>
                    </div>
                    <div className="cdb-sailing-fill">
                      <div className="cdb-fill-top">
                        <span>
                          {booked}/{total} seats
                        </span>
                        <b>{pct}%</b>
                      </div>
                      <div className="cdb-meter">
                        <span className={tone} style={{ width: `${pct}%` }} />
                      </div>
                    </div>
                  </li>
                );
              })}
            </ul>
          )}
        </article>

        <article className="cdb-card">
          <div className="cdb-card-head">
            <div>
              <h2>Needs your attention</h2>
              <p>Things to follow up on</p>
            </div>
          </div>

          {d.attention.length === 0 ? (
            <div className="cdb-empty small ok">
              <CheckCircle2 size={28} />
              <p>Nothing needs attention right now.</p>
            </div>
          ) : (
            <ul className="cdb-attention">
              {d.attention.map((a, i) => (
                <li key={`${a.text}-${i}`} className={a.tone}>
                  <span>{a.text}</span>
                  <button onClick={() => onNavigate(a.target)}>
                    {a.action} <ArrowRight size={14} />
                  </button>
                </li>
              ))}
            </ul>
          )}
        </article>
      </section>

      {/* recent bookings */}
      <section className="cdb-card">
        <div className="cdb-card-head">
          <div>
            <h2>Recent bookings</h2>
            <p>Your latest guest reservations</p>
          </div>
          <button className="cdb-link" onClick={() => onNavigate("cruise_bookings")}>
            View all <ArrowRight size={15} />
          </button>
        </div>

        {d.recent.length === 0 ? (
          <div className="cdb-empty small">
            <CalendarDays size={28} />
            <p>Bookings from guests will appear here.</p>
          </div>
        ) : (
          <div className="cdb-table-wrap">
            <table className="cdb-table">
              <thead>
                <tr>
                  <th>Booking</th>
                  <th>Guest</th>
                  <th>Sailing</th>
                  <th>Guests</th>
                  <th>Total</th>
                  <th>Status</th>
                  <th>Payment</th>
                </tr>
              </thead>
              <tbody>
                {d.recent.map((b) => (
                  <tr key={b._id} onClick={() => onNavigate("cruise_bookings")}>
                    <td>
                      <strong>{b.bookingReference || "—"}</strong>
                      <small>{dateText(b.createdAt)}</small>
                    </td>
                    <td>
                      <strong>{b.customerName || "—"}</strong>
                      <small>{b.customerPhone || b.customerEmail || "—"}</small>
                    </td>
                    <td>
                      <strong>{sailingCodeOf(b.sailingId)}</strong>
                      <small>{dateText(b.sailingId?.departureDate)}</small>
                    </td>
                    <td>{guestsOf(b)}</td>
                    <td>
                      <strong>{money(b.totalAmount)}</strong>
                    </td>
                    <td>
                      <span className={`cdb-pill st-${b.bookingStatus}`}>{b.bookingStatus}</span>
                    </td>
                    <td>
                      <span className={`cdb-pill pay-${b.paymentStatus}`}>{b.paymentStatus}</span>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </section>
    </div>
  );
}

/* =========================================================
   SMALL COMPONENTS
========================================================= */

function Kpi({ icon, tone, title, value, change, foot, onClick, highlight }) {
  return (
    <article
      className={`cdb-kpi ${highlight ? "highlight" : ""}`}
      onClick={onClick}
      role="button"
      tabIndex={0}
      onKeyDown={(e) => (e.key === "Enter" || e.key === " ") && onClick?.()}
    >
      <div className="cdb-kpi-top">
        <span className={`cdb-kpi-icon ${tone}`}>{icon}</span>
        {change && change.dir !== "flat" && (
          <span className={`cdb-delta ${change.dir}`}>
            {change.dir === "up" ? <TrendingUp size={14} /> : <TrendingDown size={14} />}
            {Math.round(change.pct)}%
          </span>
        )}
      </div>
      <span className="cdb-kpi-title">{title}</span>
      <strong className="cdb-kpi-value">{value}</strong>
      <small className="cdb-kpi-foot">{foot}</small>
    </article>
  );
}

function FleetCard({ icon, label, stat, onClick }) {
  return (
    <button className="cdb-fleet-card" onClick={onClick}>
      <span className="cdb-fleet-icon">{icon}</span>
      <span className="cdb-fleet-text">
        <b>{stat.total}</b>
        <span>{label}</span>
        <small>
          {stat.active} active{stat.draft > 0 ? ` • ${stat.draft} draft` : ""}
        </small>
      </span>
      <ArrowRight size={16} className="cdb-fleet-arrow" />
    </button>
  );
}